export function getUser() {
  try {
    return JSON.parse(localStorage.getItem('learnable_user') || 'null');
  } catch {
    return null;
  }
}

export function setUser(user) {
  localStorage.setItem('learnable_user', JSON.stringify(user));
}

export function clearAuth() {
  localStorage.removeItem('learnable_user');
}

export async function logout() {
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
  } catch { /* best-effort */ }
  clearAuth();
}

class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

let refreshPromise = null;

async function tryRefresh() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
      if (!res.ok) return null;
      const data = await res.json();
      if (data.user) setUser(data.user);
      return data;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export async function api(path, { method = 'GET', body, headers = {}, signal } = {}) {
  const isFormData = body instanceof FormData;
  const doFetch = () =>
    fetch(path, {
      method,
      credentials: 'include',
      headers: {
        ...(body && !isFormData && { 'Content-Type': 'application/json' }),
        ...headers,
      },
      body: isFormData ? body : body ? JSON.stringify(body) : undefined,
      signal,
    });

  let res = await doFetch();

  if (res.status === 401) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await doFetch();
    } else {
      clearAuth();
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      }
      throw new ApiError('unauthorized', 401, {});
    }
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(data.error || `HTTP ${res.status}`, res.status, data);
  return data;
}

export function apiUpload(path, formData, { onProgress, signal } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path, true);
    xhr.withCredentials = true;

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    if (signal) {
      signal.addEventListener('abort', () => xhr.abort());
    }
    xhr.onload = () => {
      let body = {};
      try { body = JSON.parse(xhr.responseText); } catch {}
      if (xhr.status >= 200 && xhr.status < 300) return resolve(body);
      if (xhr.status === 401) {
        tryRefresh().then((refreshed) => {
          if (!refreshed) {
            clearAuth();
            window.location.href = '/login';
          } else {
            const retry = new XMLHttpRequest();
            retry.open('POST', path, true);
            retry.withCredentials = true;
            if (onProgress) {
              retry.upload.onprogress = (e) => {
                if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
              };
            }
            retry.onload = () => {
              let b = {};
              try { b = JSON.parse(retry.responseText); } catch {}
              if (retry.status >= 200 && retry.status < 300) return resolve(b);
              reject(new ApiError(b.error || `HTTP ${retry.status}`, retry.status, b));
            };
            retry.onerror = () => reject(new ApiError('network_error', 0, {}));
            retry.send(formData);
          }
        });
        return;
      }
      reject(new ApiError(body.error || `HTTP ${xhr.status}`, xhr.status, body));
    };
    xhr.onerror = () => reject(new ApiError('network_error', 0, {}));
    xhr.onabort = () => reject(new ApiError('aborted', 0, {}));
    xhr.send(formData);
  });
}

// `preamble` is an optional list of rows rendered above the header line — each
// entry is an array of cells (or a single string) and is padded to the table
// width. Used by the roster export to stamp the session date, course title, and
// provider license numbers before the column headers.
export function downloadCsv(rows, filename, preamble = []) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const padRow = (cells) => {
    const list = Array.isArray(cells) ? cells : [cells];
    const padded = list.slice(0, headers.length);
    while (padded.length < headers.length) padded.push('');
    return padded.map(escape).join(',');
  };
  const csv = [
    ...preamble.map(padRow),
    headers.map(escape).join(','),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(',')),
  ].join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function formatMoney(cents, currency = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format((cents || 0) / 100);
}

export function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

export function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}
